import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';

// Rediseño · Bloque A. Las variantes son las de las pantallas aprobadas, ni una más:
//   primary   — la acción de la pantalla (azul sólido).
//   secondary — la alternativa (blanco con borde).
//   tonal     — acción de apoyo (azul suave).
//   ghost     — acción en texto, sin caja.
//   danger    — acción destructiva EN TEXTO («Vaciar», «Cancelar venta»). Las pantallas aprobadas no
//               tienen un botón rojo sólido, así que aquí tampoco lo hay.
export type ButtonVariant = 'primary' | 'secondary' | 'tonal' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** `lg` es el botón grande al pie de una pantalla de teléfono (56 px). */
  size?: ButtonSize;
  fullWidth?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  /**
   * Mientras es `true` el botón queda apagado (no admite un segundo toque) y se anuncia ocupado.
   * Una espera siempre se dice con palabras, no solo con un giro: pasa `loadingLabel`.
   */
  loading?: boolean;
  /** Texto que sustituye al del botón mientras `loading` («Guardando…», «Esperando al servidor…»). */
  loadingLabel?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', fullWidth = false, iconStart, iconEnd, loading = false, loadingLabel, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      aria-busy={loading || undefined}
      className={cx('n3-button', `n3-button--${variant}`, size === 'lg' && 'n3-button--lg', fullWidth && 'n3-button--full', className)}
      disabled={disabled || loading}
      ref={ref}
      type={type}
    >
      {loading
        ? <span aria-hidden="true" className="n3-button__spinner" />
        : iconStart && <span aria-hidden="true" className="n3-button__icon">{iconStart}</span>}
      <span className="n3-button__label">{loading && loadingLabel ? loadingLabel : children}</span>
      {!loading && iconEnd && <span aria-hidden="true" className="n3-button__icon">{iconEnd}</span>}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'aria-labelledby'> {
  /** Obligatorio: un botón que solo muestra un icono necesita un nombre que se pueda leer en voz alta. */
  label: string;
  /** `surface` es un mosaico blanco con sombra, para ir sobre el lienzo; `soft` es gris y sin sombra, para ir sobre una superficie blanca (cerrar un panel). */
  variant?: 'surface' | 'soft';
}

// Siempre mide 44 × 44: es el mínimo táctil, y un botón de icono es el más fácil de fallar con el dedo.
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'surface', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      aria-label={label}
      className={cx('n3-icon-button', variant === 'soft' && 'n3-icon-button--soft', className)}
      ref={ref}
      type={type}
    >
      {children}
    </button>
  );
});
